import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useIntl } from 'react-intl';

import type { ContainerModule, ContainerModules, ModuleLesson } from '@yandex-int/k-common';
import { Button } from '@edu-frontend/modules/src/components/ui/Button';
import { IconArrowShortRight } from '@edu-frontend/modules/src/components/ui/Icon';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';
import { Text as HrText } from '@edu-frontend/modules/src/components/ui/Text';
import { IconClock } from '@edu-frontend/modules/src/shared/ui/Icon';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import type { LessonCardProps } from '@edu-frontend/modules/src/entities/lesson/ui/LessonCard/LessonCard';
import { LessonCardsList } from '@edu-frontend/modules/src/entities/lesson/ui/LessonCardsList/LessonCardsList';
import type { LessonStatusProps } from '@edu-frontend/modules/src/entities/lesson/ui/LessonStatus/LessonStatus';
import cn from 'classnames';
import { Spin, SPIN_SIZES, Text } from 'platform-components';
import groupBy from 'lodash/groupBy';
import qs from 'query-string';

import { SEARCH_GROUP_INDEX } from 'common.components/constants';
import { getCurrentYearCourseId } from 'common.components/course/selectors';

import { getOpenedModule } from '../../../library.course-container/selectors';
import { useCourseContainerModuleIndexes } from '../../../library.course-container/__modules/course-container__modules';
import { useModuleLessonsQuery } from '../../../library.course-container/queries';
import { useOpenLessonCallback } from '../../../library.course-container/__lesson/course-container__lesson';

import './library__informatics__modules.scss';

interface Props {
    /**
     * Данные модулей для отображения
     */
    modules: ContainerModules;
}

interface HeaderProps {
    /**
     * Данные модуля
     */
    containerModule: ContainerModule;
    /**
     * Номер параграфа
     */
    openModuleOrder: number;
    /**
     * Номер модуля в параграфе
     */
    openModuleOrderFromActiveGroup: number;
}

interface LessonsGroupsProps {
    /**
     * Данные модуля
     */
    containerModule: ContainerModule;
    /**
     * Индекс открытой группы уроков (для попапа группы уроков)
     */
    groupIndex?: number;
}

interface LessonsGroupProps {
    /**
     * Имя урока
     */
    name?: string;

    /**
     * Описание урока
     */
    description?: string;

    /**
     * Данные задач
     */
    lessons: ModuleLesson[];
    className?: string;
    /** при наличии обработчика, становится активна кнопка в заголовке */
    onMenuClick?: () => void;
    'data-testid'?: string;
}

export const Library__informatics__modules = ({ modules }: Props) => {
    const openedModuleId = useSelector(getOpenedModule);

    const { openedModule, openedModuleOrder, openModuleOrderFromActiveGroup } = useCourseContainerModuleIndexes(
        modules,
        openedModuleId
    );

    const { search } = useLocation();
    const params = qs.parse(search);
    const groupIndex = params[SEARCH_GROUP_INDEX];

    if (!openedModule) {
        return null;
    }

    return (
        <ThemeProvider isRoot={false} theme={hrLight}>
            {!groupIndex && (
                <CourseContainerModuleHeader
                    containerModule={openedModule}
                    openModuleOrder={openedModuleOrder}
                    openModuleOrderFromActiveGroup={openModuleOrderFromActiveGroup}
                />
            )}
            <CourseContainerModuleLessons
                containerModule={openedModule}
                groupIndex={groupIndex ? Number(groupIndex) : undefined}
            />
        </ThemeProvider>
    );
};

const CourseContainerModuleHeader = ({
    containerModule,
    openModuleOrder,
    openModuleOrderFromActiveGroup,
}: HeaderProps) => {
    return (
        <div className="library__informatics_module_container">
            <HrText color="secondary" typography="bodyS">
                Модуль
            </HrText>
            <HrText color="primary" typography="titleM">
                {`${openModuleOrder}.${openModuleOrderFromActiveGroup} ${containerModule.module.name}`}
            </HrText>
            <Text size={Text.SIZE.M} type={Text.TYPE.MARKDOWN}>
                {containerModule.module.annotation}
            </Text>
        </div>
    );
};

const CourseContainerModuleLessons = (props: LessonsGroupsProps) => {
    const { containerModule, groupIndex } = props;
    const moduleId = containerModule.module.id;
    const courseId = useSelector(getCurrentYearCourseId)!;
    const lessonsQuery = useModuleLessonsQuery(moduleId, courseId);
    const results = lessonsQuery.data?.results;

    const lessonsGroups = useMemo(() => {
        const groups = Object.entries(groupBy(results || [], 'group_name'));

        if (groupIndex !== undefined && groups[groupIndex]) {
            return [groups[groupIndex]];
        }

        return groups;
    }, [results, groupIndex]);

    return (
        <div className="library__informatics__modules__module-lessons">
            {lessonsQuery.isLoading && <Spin center size={SPIN_SIZES.L} />}
            {lessonsQuery.isSuccess &&
                lessonsGroups.map(([groupName, lessons]) => (
                    <CourseContainerModuleLessonsGroup key={groupName} lessons={lessons} name={groupName} />
                ))}
        </div>
    );
};

export const CourseContainerModuleLessonsGroup = (props: LessonsGroupProps) => {
    const { name, description, lessons, className, onMenuClick } = props;
    const intl = useIntl();
    const openLessonCallback = useOpenLessonCallback();

    const { search } = useLocation();
    const { module_id, group_index } = qs.parse(search);
    const isOpenedFromLessonGroup = module_id !== undefined && group_index !== undefined;

    const items: LessonCardProps[] = useMemo(() => {
        return lessons.map((lesson) => {
            let status: LessonCardProps['status'];
            if (lesson.assigned_count) {
                status = 'assigned';
            }
            if (lesson.finished_count && lesson.assigned_count === lesson.finished_count) {
                status = 'finished';
            }

            return {
                onClick: openLessonCallback(lesson.lesson.id, {
                    from: isOpenedFromLessonGroup ? 'lesson_group' : 'module',
                    subject: 'informatics',
                }),
                description: intl.formatMessage(
                    { id: 'library.prepLessons.problemsCount' },
                    { count: lesson.problems_count }
                ),
                id: String(lesson.lesson.id),
                methodologicalGoal: lesson.lesson.meta.methodological_goal
                    ? intl.formatMessage({
                          id: `lesson.lessonMeta.methodologicalGoals.${lesson.lesson.meta.methodological_goal}`,
                      })
                    : undefined,
                status,
                methodologicalGoalColor: lesson.lesson.meta.methodological_goal
                    ? (intl.formatMessage({
                          id: `lesson.lessonMeta.methodologicalGoals.${lesson.lesson.meta.methodological_goal}.color`,
                      }) as LessonStatusProps['color'])
                    : undefined,
                name: lesson.lesson.name,

                timeText: lesson.lesson.meta.duration
                    ? intl.formatMessage({ id: 'lesson-meta.duration' }, { duration: lesson.lesson.meta.duration })
                    : undefined,
                timeIcon: <IconClock />,
                buttonAriaLabel: intl.formatMessage({ id: 'library.lessonCard.buttonAriaLabel' }),
            };
        });
    }, [intl, lessons, openLessonCallback, isOpenedFromLessonGroup]);

    return (
        <div className={className} data-testid={props['data-testid']}>
            <div className="library__informatics_group_header">
                <Button
                    className={cn(onMenuClick ?? 'library__informatics_disabled-button')}
                    icon={(classNames: string) => <IconArrowShortRight className={classNames} size="s" />}
                    onClick={onMenuClick}
                    size="s"
                    view="outline"
                />
                <div className="library__informatics__modules__header-description  ">
                    {description && (
                        <HrText color="secondary" typography="captionM">
                            {description}
                        </HrText>
                    )}
                    {name && (
                        <Text size={Text.SIZE.XL} type={Text.TYPE.MARKDOWN}>
                            {name}
                        </Text>
                    )}
                </div>
            </div>
            <LessonCardsList items={items} />
        </div>
    );
};
